import { Hidden } from '@material-ui/core';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { FormikHelpers } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { ForbiddenErrorMessage } from '../../../../global/components/forbidden-error-message';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { PageContainer } from '../../../../global/components/layout/page-container';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { useParentRoute } from '../../../../global/hooks/useParentRoute';
import { ApiStatuses, BaseApiState } from '../../../../global/models/api-model';
import { TcsmEpiApiState, TcsmStates } from '../../../../root/tcsm-model';
import { ArcRoutes } from '../../constants/routes';
import { InstitutionState } from '../institutions/institution-model';
import { PersonSearchContext, PersonSearchKeys, SearchPerson } from '../person-search/person-search-model';
import { OfficerAdminState } from './institution-officers-models';
import { setAddOfficerStatus } from './institution-officers-slices';
import { OfficerAddForm } from './officer-add-form';
import { OfficerAddFormValues } from './officer-add-form-model';
import { addNonSponsoredOfficerRequest, addSponsoredOfficerRequest } from './officer-add-requests';
import { OfficerAddSuccess } from './officer-add-success';
import { OSCRRService } from '../person-search/oscrr-helpers';
import { useRoleObject } from '../../components/useRoles';
import { SeniorOfficer } from '../../constants/constant-strings';

function NotEmptyAndNotEqual(first: string, second: string) {
	return first && (first === second);
}

function AddOfficer() {

	const apiState = useSelector<TcsmStates, BaseApiState>(state => state.apiState);

	const officersState = useSelector<TcsmStates, OfficerAdminState>(state => state.officerAdminState);
	const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const institutionProps = epiProps?.institutionSettingsState?.epiContent;
	const pageRef = useRef<HTMLDivElement>(null);
	const parentRoute = useParentRoute();
	const dispatch = useDispatch();
	const location = useLocation();

	const [showConfirmation, setShowConfirmation] = useState(false);
	const [submittedName, setSubmittedName] = useState('');
	const roles = useRoleObject();

	let gpsType = showConfirmation ? GpsType.ADD_INSTITUTION_ADMINISTRATOR_REVIEW : GpsType.INSTITUTION_ADMINISTRATOR;

	if (officersState.addOfficerStatus === ApiStatuses.Success) {
		gpsType = GpsType.ADD_INSTITUTION_ADMINISTRATOR_CONFIRMATION;
	}

	const gpsItem = useGpsType(gpsType);

	useEffect(() => {
		dispatch(apiErrorClear());

		return function cleanup() {
			dispatch(setAddOfficerStatus(ApiStatuses.None));
		};

	}, []);

	// restrict access to officer add feature by roles
	if (!(roles?.employee_roster?.map(a => a.role_name)?.includes(SeniorOfficer))) {
		return <ForbiddenErrorMessage code={403} />;
	}

	if (!institutionState?.institutionClientNumber) {
		const institutionSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionSearch}`);

		return (
			<Redirect to=
				{
					{
						pathname: institutionSelect,
						state: {
							actionLink: location.pathname,
						},
					}
				}
			/>
		);
	}


	const onSubmit = async (values: OfficerAddFormValues, savedValues: OfficerAddFormValues, formikHelpers: FormikHelpers<OfficerAddFormValues>, selectedPerson: SearchPerson) => {


		if (!showConfirmation) {
			setShowConfirmation(true);
			window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
			dispatch(apiErrorClear());
			formikHelpers.setTouched({});
			formikHelpers.setSubmitting(false);

			return;
		}

		setSubmittedName(`${values.first_name} ${values.last_name}`);

		if (selectedPerson) {
			const addedOfficeEmail = NotEmptyAndNotEqual(selectedPerson.office_email?.email_address, values.office_email_address) ? '' : values.office_email_address;
			const addedPersonEmail = NotEmptyAndNotEqual(selectedPerson.personal_email?.email_address, values.personal_email_address) ? '' : values.personal_email_address;
			addNonSponsoredOfficerRequest(dispatch, values.officerType, selectedPerson.guid, institutionState?.currentInstitution?.org_client_nbr, addedOfficeEmail, addedPersonEmail, values.withAccessTo);
		} else {
			addSponsoredOfficerRequest(dispatch, savedValues, values, institutionState?.currentInstitution?.org_client_nbr);
		}

		setShowConfirmation(false);
	};

	const renderContent = () => {
		if (officersState.addOfficerStatus === ApiStatuses.Success) {
			return (
				<OfficerAddSuccess
					institutionProps={institutionProps}
					name={submittedName}
					officerUrl={parentRoute}
					tempPassword={officersState?.addAdminOfficerTempPw}
				/>
			);
		}

		return (
			<PersonSearchContext.Provider value={PersonSearchKeys.OfficerAdd}>
				{(OSCRRService.IsOSCRRWorking(apiState) ? <LoadingCircle /> : null)}
				<OfficerAddForm
					onSubmitHandler={onSubmit}
					labelSetting={epiProps?.labelSettingState?.epiContent}
					institutionProps={institutionProps}
					previousRoute={parentRoute}
					showConfirmation={showConfirmation}
					setShowConfirmation={setShowConfirmation} />

			</PersonSearchContext.Provider>
		);
	};

	return (
		<>
			<PageContainer>
				<MainContent>
					<div ref={pageRef}>
						<PageHeader
							headerText={showConfirmation ? institutionProps?.businessEmailReviewTitle : institutionProps?.addOfficerTitle}
							description={showConfirmation ? institutionProps?.businessEmailReviewDescription : institutionProps?.addOfficerDescription}
						/>
						<Hidden mdUp>
							<SideBarContent>
								<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
							</SideBarContent>
						</Hidden>
						{renderContent()}
					</div>
				</MainContent>
				<Hidden smDown>
					<SideBarContent>
						<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
					</SideBarContent>
				</Hidden>
			</PageContainer>
		</>
	);
}

export default AddOfficer;